// src/frontend/js/modern/lib/sortable-lite.js
//
// Lightweight jQuery-UI-Sortable replacement.
// The floating helper is a mini-table clone that reuses the SAME view-mode
// classes as the original table, so all existing CSS applies automatically
// without any duplicated styles.
export class SortableLite {
	constructor(container, options = {}) {
		this.container = container;
		this.options = {
			items: '[data-row-id]',
			handle: '.sortable-handle',
			placeholderClass: 'ui-sortable-placeholder',
			scrollSensitivity: 40,
			dragStartDistance: 5,
			dropAnimationMs: 100,
			update: null,
			...options
		};
		this._pendingItem = null;
		this._dragging = null;
		this._placeholder = null;
		this._helperWrapper = null;
		this._started = false;
		this._startX = 0;
		this._startY = 0;
		this._offsetX = 0;
		this._offsetY = 0;
		this._currentX = 0;
		this._currentY = 0;
		this._onMouseDown = this._onMouseDown.bind(this);
		this._onMouseMove = this._onMouseMove.bind(this);
		this._onMouseUp = this._onMouseUp.bind(this);
		this._init();
	}

	_init() {
		this.container.addEventListener('mousedown', this._onMouseDown);
		this.container.style.userSelect = 'none';
	}

	_getItems() {
		return Array.from(this.container.querySelectorAll(this.options.items));
	}

	/**
	 * Detect the view-mode class from the parent table.
	 */
	_getViewModeClass() {
		const table = this.container.closest('.wlfmc-wishlist-table, .wlfmc-save-for-later-table');
		if (table && table.classList.contains('view-mode-grid')) return 'view-mode-grid';
		return 'view-mode-list';
	}

	/**
	 * Build the floating helper as a mini-table that reuses the original
	 * table's view-mode classes. All existing CSS applies automatically.
	 */
	_createHelper(item) {
		const rect = item.getBoundingClientRect();
		const viewModeClass = this._getViewModeClass();

		const wrapper = document.createElement('div');
		wrapper.className = 'wlfmc-sortable-helper';
		wrapper.style.width = rect.width + 'px';
		wrapper.style.top = rect.top + 'px';
		wrapper.style.left = rect.left + 'px';

		const miniTable = document.createElement('table');
		miniTable.className = 'wlfmc-wishlist-table  wlfmc-list ' + viewModeClass;
		miniTable.style.borderSpacing = '0';
		miniTable.style.margin = '0';
		miniTable.style.width = '100%';
		// Force cells to respect explicit widths
		miniTable.style.tableLayout = 'fixed';

		const tbody = document.createElement('tbody');
		tbody.className = 'wishlist-items-wrapper';

		const clone = item.cloneNode(true);
		clone.classList.remove('show-meta-data', 'ui-sortable-helper');
		clone.removeAttribute('style');

		// Lock each cell width BEFORE removing the handle (indices must match)
		const srcCells = item.querySelectorAll('td');
		/*const cloneCells = clone.querySelectorAll('td');
		srcCells.forEach((td, i) => {
			if (cloneCells[i]) {
				cloneCells[i].style.width = td.getBoundingClientRect().width + 'px';
			}
		});*/

		// Remove the drag handle cell (purely interactive, not visual)
		const handleCell = clone.querySelector('td.sortable-handle');
		if (handleCell) handleCell.remove();

		tbody.appendChild(clone);
		miniTable.appendChild(tbody);
		wrapper.appendChild(miniTable);
		document.body.appendChild(wrapper);

		return wrapper;
	}

	_onMouseDown(e) {
		if (e.button !== 0) return;
		const handle = e.target.closest(this.options.handle);
		if (!handle) return;
		const item = handle.closest(this.options.items);
		if (!item || !this.container.contains(item)) return;
		e.preventDefault();

		this._pendingItem = item;
		this._started = false;
		this._startX = e.clientX;
		this._startY = e.clientY;
		const rect = item.getBoundingClientRect();
		this._offsetX = e.clientX - rect.left;
		this._offsetY = e.clientY - rect.top;

		document.addEventListener('mousemove', this._onMouseMove);
		document.addEventListener('mouseup', this._onMouseUp);
	}

	_startDrag(item) {
		this._started = true;
		this._dragging = item;
		const rect = item.getBoundingClientRect();

		// Placeholder holds the slot
		const placeholder = document.createElement('tr');
		placeholder.className = this.options.placeholderClass;
		placeholder.style.height = rect.height + 'px';
		item.parentNode.insertBefore(placeholder, item);
		this._placeholder = placeholder;
		if (this._getViewModeClass() === 'view-mode-grid') {
			// Find any existing item to get the card dimensions
			const anyItem = this._getItems().find(it => it !== item);
			if (anyItem) {
				const cardRect = anyItem.getBoundingClientRect();
				placeholder.style.width = cardRect.width + 'px';
				placeholder.style.height = cardRect.height + 'px';
				placeholder.style.display = 'inline-block';
			}
		}

		// Build floating helper
		this._helperWrapper = this._createHelper(item );

		// Hide original row
		item.style.display = 'none';

		this._hideMetaDataDuringDrag(item);
		this.container.classList.add('wlfmc-sortable-active');
	}

	_onMouseMove(e) {
		const item = this._pendingItem;
		if (!item) return;

		if (!this._started) {
			const dx = e.clientX - this._startX;
			const dy = e.clientY - this._startY;
			if (Math.hypot(dx, dy) < this.options.dragStartDistance) return;
			this._startDrag(item);
		}

		e.preventDefault();

		this._currentX = e.clientX;
		this._currentY = e.clientY;

		// Move helper
		if (this._helperWrapper) {
			this._helperWrapper.style.left = (e.clientX - this._offsetX) + 'px';
			this._helperWrapper.style.top = (e.clientY - this._offsetY) + 'px';
		}

		this._autoScroll(e.clientY);
		this._updatePlaceholder(e.clientY);
	}

	_autoScroll(y) {
		const sens = this.options.scrollSensitivity;
		const vh = window.innerHeight;
		if (y < sens) {
			window.scrollBy(0, -10);
		} else if (y > vh - sens) {
			window.scrollBy(0, 10);
		}
	}

	_updatePlaceholder(y) {
		const placeholder = this._placeholder;
		if (!placeholder) return;
		const items = this._getItems().filter(it => it !== this._dragging);

		if (items.length === 0) {
			const tbody = this.container.querySelector('tbody') || this.container;
			tbody.appendChild(placeholder);
			return;
		}

		const isGrid = this._getViewModeClass() === 'view-mode-grid';

		if (!isGrid) {
			// ═══ LIST MODE: vertical insertion (existing logic) ═══
			let target = null;
			for (const item of items) {
				const rect = item.getBoundingClientRect();
				if (y < rect.top + rect.height / 2) {
					target = item;
					break;
				}
			}
			if (target) {
				if (placeholder.nextElementSibling !== target) {
					target.parentNode.insertBefore(placeholder, target);
				}
			} else {
				const last = items[items.length - 1];
				if (placeholder.previousElementSibling !== last) {
					last.parentNode.insertBefore(placeholder, last.nextSibling);
				}
			}
			return;
		}

		// ═══ GRID MODE: 2D insertion between columns AND rows ═══
		const x = this._currentX;

		// 1) Find the closest card by Euclidean distance to its center
		let closestItem = null;
		let closestDistance = Infinity;
		let closestRect = null;

		for (const item of items) {
			const rect = item.getBoundingClientRect();
			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;
			const dx = x - centerX;
			const dy = y - centerY;
			const distance = dx * dx + dy * dy;
			if (distance < closestDistance) {
				closestDistance = distance;
				closestItem = item;
				closestRect = rect;
			}
		}

		if (!closestItem) return;

		// 2) Diagonal split: divide the closest card into two triangular halves
		//    using the diagonal from top-right to bottom-left.
		//    Cursor in the top-left triangle  → insert BEFORE the card
		//    Cursor in the bottom-right triangle → insert AFTER the card
		//
		//    Diagonal equation at given x:
		//    y_diag = top + (right - x) / width * height
		const diagY = closestRect.top +
			(closestRect.right - x) / closestRect.width * closestRect.height;
		const shouldInsertBefore = y < diagY;

		// 3) Insert placeholder accordingly
		if (shouldInsertBefore) {
			if (placeholder.nextElementSibling !== closestItem) {
				closestItem.parentNode.insertBefore(placeholder, closestItem);
			}
		} else {
			const nextSibling = closestItem.nextElementSibling;
			if (placeholder.nextSibling !== nextSibling) {
				closestItem.parentNode.insertBefore(placeholder, nextSibling);
			}
		}

		// 4) Size the placeholder to match a single card (not full table width)
		placeholder.style.width = closestRect.width + 'px';
		placeholder.style.height = closestRect.height + 'px';
		placeholder.style.display = 'inline-block';
	}

	_onMouseUp(e) {
		document.removeEventListener('mousemove', this._onMouseMove);
		document.removeEventListener('mouseup', this._onMouseUp);

		const item = this._pendingItem;
		this._pendingItem = null;
		if (!item || !this._started) return;

		const helperWrapper = this._helperWrapper;
		const placeholder = this._placeholder;
		const dropRect = placeholder ? placeholder.getBoundingClientRect() : null;

		// Place real row into the slot, keep invisible during animation
		if (placeholder && placeholder.parentNode) {
			item.style.display = '';
			item.style.visibility = 'hidden';
			placeholder.parentNode.insertBefore(item, placeholder);
			placeholder.remove();
		}
		this._placeholder = null;
		this._dragging = null;
		this.container.classList.remove('wlfmc-sortable-active');

		this._renumberPositions();
		this._repositionChildRows();

		const finalize = () => {
			item.style.visibility = '';
			if (helperWrapper && helperWrapper.parentNode) helperWrapper.remove();
			this._helperWrapper = null;
		};

		if (helperWrapper && dropRect) {
			const duration = this.options.dropAnimationMs;
			const fadeDelay = Math.round(duration * 0.4);   // fade starts at 40%
			const fadeDuration = duration - fadeDelay;        // fade lasts for the remaining 60%

			// 1) Set transition FIRST (before any property change).
			//    Opacity has a delay so the helper glides into place BEFORE fading.
			helperWrapper.style.transition =
				`top ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), ` +
				`left ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), ` +
				`opacity ${fadeDuration}ms ease-in ${fadeDelay}ms, ` +
				`transform ${duration}ms ease-out`;

			// 2) Double rAF ensures the browser registers the transition
			//    before we change the animated properties.
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					helperWrapper.style.top = dropRect.top + 'px';
					helperWrapper.style.left = dropRect.left + 'px';
					helperWrapper.style.opacity = '0';
					helperWrapper.style.transform = 'scale(0.96)';
				});
			});

			// 3) Precise end detection via transitionend + safety timeout.
			let finalized = false;
			const onTransitionEnd = (ev) => {
				if (ev.propertyName !== 'top') return;
				if (finalized) return;
				finalized = true;
				helperWrapper.removeEventListener('transitionend', onTransitionEnd);
				finalize();
			};
			helperWrapper.addEventListener('transitionend', onTransitionEnd);

			// Fallback if transitionend never fires (e.g. element hidden/removed).
			setTimeout(() => {
				if (!finalized) {
					finalized = true;
					helperWrapper.removeEventListener('transitionend', onTransitionEnd);
					finalize();
				}
			}, duration + 100);
		} else {
			finalize();
		}

		if (typeof this.options.update === 'function') {
			this.options.update(e, { item });
		}
	}

	_hideMetaDataDuringDrag(draggedItem) {
		const wrapper = draggedItem.closest('.wishlist-items-wrapper');
		if (!wrapper) return;
		wrapper.querySelectorAll('.show-meta-data').forEach(el => {
			el.classList.remove('show-meta-data');
		});
		wrapper.querySelectorAll('.wlfmc-row-meta-data').forEach(el => {
			el.classList.add('hide');
		});
	}

	_repositionChildRows() {
		const items = this._getItems();
		items.forEach(row => {
			const rowId = row.getAttribute('data-row-id');
			if (!rowId) return;
			const wrapper = row.closest('.wishlist-items-wrapper');
			if (!wrapper) return;
			const childRows = wrapper.querySelectorAll(`.parent-row-id-${rowId}`);
			if (childRows.length > 0) {
				childRows.forEach(child => {
					row.after(child);
				});
			}
		});
	}

	_renumberPositions() {
		this.container.querySelectorAll('input[name*="[position]"]').forEach((input, idx) => {
			input.value = String(idx);
		});
	}

	destroy() {
		this.container.removeEventListener('mousedown', this._onMouseDown);
		document.removeEventListener('mousemove', this._onMouseMove);
		document.removeEventListener('mouseup', this._onMouseUp);
		this.container.style.userSelect = '';
	}
}
